// One store fed by the socket. The server pushes the whole projected view on every change, so
// there is no cache to invalidate and no optimistic state to reconcile — the view in here is
// simply the last thing the server said, and every action is a fire-and-forget emit whose ack
// only ever carries a refusal message.

import { io, type Socket } from "socket.io-client";
import { create } from "zustand";
import type { ClientEventName, ClientPayload, GameView, Notice } from "@cc/shared";

export type Ack = { ok: true; code?: string } | { ok: false; error: string };

type GameStore = {
  socket: Socket | null;
  connected: boolean;
  view: GameView | null;
  notices: (Notice & { id: number })[];
  lastError: string | null;
  /** Server time minus browser time, so countdowns are drawn against the server's clock. */
  clockSkewMs: number;

  connect: (token: string) => void;
  disconnect: () => void;
  send: <E extends ClientEventName>(event: E, payload?: ClientPayload<E>) => Promise<Ack>;
  createGame: (opts: { guildId: string; mainChannelId: string | null; housesCategoryId: string | null }) => Promise<Ack>;
  /** Omit asStoryteller to rejoin as whatever you already were — see the protocol note. */
  joinGame: (code: string, asStoryteller?: boolean) => Promise<Ack>;
  leaveGame: () => void;
  dismissNotice: (id: number) => void;
  clearError: () => void;
};

let noticeSeq = 0;

export const useGame = create<GameStore>((set, get) => ({
  socket: null,
  connected: false,
  view: null,
  notices: [],
  lastError: null,
  clockSkewMs: 0,

  connect: (token: string) => {
    const existing = get().socket;
    if (existing) {
      if (existing.auth && typeof existing.auth === "object") {
        (existing.auth as Record<string, unknown>)["token"] = token;
      }
      if (!existing.connected) existing.connect();
      return;
    }
    const socket = io({ auth: { token }, transports: ["websocket", "polling"] });

    socket.on("connect", () => set({ connected: true }));
    socket.on("disconnect", () => set({ connected: false }));
    socket.on("connect_error", (err: Error) => set({ connected: false, lastError: err.message }));
    socket.on("state:sync", (view: GameView) => {
      set({ view, clockSkewMs: view.serverTime - Date.now() });
    });
    socket.on("notice", (notice: Notice) => {
      const entry = { ...notice, id: ++noticeSeq };
      set((s) => ({ notices: [...s.notices, entry].slice(-4) }));
      setTimeout(() => get().dismissNotice(entry.id), 6000);
    });
    socket.on("error", (payload: { error: string }) => set({ lastError: payload.error }));

    set({ socket });
  },

  disconnect: () => {
    get().socket?.disconnect();
    set({ socket: null, connected: false, view: null });
  },

  send: async (event, payload) => {
    const socket = get().socket;
    if (!socket) return { ok: false, error: "Not connected." };
    return new Promise<Ack>((resolve) => {
      socket.emit(event, payload ?? {}, (ack: Ack) => {
        if (ack && !ack.ok) set({ lastError: ack.error });
        resolve(ack ?? { ok: true });
      });
    });
  },

  createGame: async (opts) => get().send("game:create", opts),
  joinGame: async (code, asStoryteller) =>
    get().send("game:join", asStoryteller === undefined ? { code } : { code, asStoryteller }),
  leaveGame: () => {
    void get().send("game:leave", {});
    set({ view: null });
  },

  dismissNotice: (id) => set((s) => ({ notices: s.notices.filter((n) => n.id !== id) })),
  clearError: () => set({ lastError: null }),
}));

/** Server-aligned "now", used by every countdown and the vote clock. */
export function serverNow(): number {
  return Date.now() + useGame.getState().clockSkewMs;
}
